import store from "@/src/store";
import { getSourceOptionsFromAttribute } from "@/src/utils/schemaUtils.js";
import CRMService from "@/src/api/CRMService.js";
import TicketsService from "@/src/api/TicketsService.js";
import ProjectService from "@/src/api/ProjectService.js";
import DashboardService from "@/src/api/DashboardService.js";
import { entityTypes } from "@/src/constants/entityTypeConstants";
import {
  standardObjectStatusFields,
} from "@/src/constants/crmConstants.js";
import {
  standardObjects,
} from "@/src/constants/objectConstants.js";
import { toSnakeCase } from "@/src/utils/analyticUtils.js";

const buildSavedOptionsMap = (field, currentView, preserveSavedConfig) => {
  const savedConfig = preserveSavedConfig ? currentView?.group_by_config : null;
  const savedOptions =
    savedConfig?.attribute_slug === field.slug ||
    savedConfig?.attribute_slug === field.id
      ? savedConfig.options || []
      : [];

  const map = new Map();
  savedOptions.forEach((opt) => map.set(opt.id, opt));
  return map;
};

const formatGroupByOptions = (
  rawOptions,
  savedOptionsMap,
  { idKey = "id", nameKey = "name" } = {},
) => {
  return rawOptions
    .map((option, index) => {
      const optionId = option[idKey];
      const savedOption = savedOptionsMap.get(optionId);

      return {
        id: optionId,
        value: option[nameKey],
        visible: savedOption ? savedOption.is_visible : true,
        apiId: optionId,
        position: savedOption ? savedOption.position : index,
      };
    })
    .sort((a, b) => a.position - b.position);
};

const HARDCODED_GROUP_BY_FIELDS = {};

export const updateViewsWithNewData = (views, updatedView) => {
  if (!updatedView || !views) {
    return views;
  }

  const updatedActiveViews = views.active.map((view) => {
    if (view.id === updatedView.id) {
      return { ...view, ...updatedView };
    }
    return view;
  });

  return {
    ...views,
    active: updatedActiveViews,
    current: { ...views.current, ...updatedView },
  };
};

const normalizeFilterData = (filters, schemaObjectData) => {
  if (!filters || typeof filters !== "object") return {};

  return { ...filters };
};

export const applyViewConfiguration = (
  view,
  state,
  tableHeaders = null,
  schemaObjectData = null,
  applyFrozenColumnsToHeaders = null,
  applyColumnWidthsToHeaders = null,
) => {
  if (!view) return;

  const {
    filterData,
    sortData,
    pagination,
    frozenColumns,
    columns,
    kanbanColumns,
  } = state;

  if (filterData) {
    filterData.value = normalizeFilterData(
      view.filters || {},
      schemaObjectData,
    );
  }

  if (sortData) {
    sortData.value = view.sorts || {};
  }

  if (pagination) {
    pagination.value = {
      ...pagination.value,
      pageSize: view.page_size || view.pageSize || 20,
      current: view.current_page || view.currentPage || 1,
    };
  }

  const idToSlugMap = {};
  if (schemaObjectData?.value?.properties) {
    schemaObjectData.value.properties.forEach((prop) => {
      if (prop.id && prop.slug) {
        idToSlugMap[prop.id] = prop.slug;
      }
    });
  }

  if (columns && view.columns && Array.isArray(view.columns)) {
    const hasMapping = Object.keys(idToSlugMap).length > 0;

    const convertedColumns = view.columns
      .map((id) => {
        const slug = idToSlugMap[id];

        if (slug) return slug;
        if (hasMapping && schemaObjectData?.value?.properties) {
          const isSlug = schemaObjectData.value.properties.some(
            (prop) => prop.slug === id,
          );
          if (isSlug) {
            return id;
          }
          return null;
        }

        return id;
      })
      .filter((col) => col !== null && col !== undefined);

    columns.value = convertedColumns;
  }

  // Handle kanban_columns for board views
  if (
    kanbanColumns &&
    view.kanban_columns &&
    Array.isArray(view.kanban_columns)
  ) {
    const hasMapping = Object.keys(idToSlugMap).length > 0;

    const convertedKanbanColumns = view.kanban_columns
      .map((id) => {
        const slug = idToSlugMap[id];

        if (slug) return slug;
        if (hasMapping && schemaObjectData?.value?.properties) {
          const isSlug = schemaObjectData.value.properties.some(
            (prop) => prop.slug === id,
          );
          if (isSlug) {
            return id;
          }
          return null;
        }

        return id;
      })
      .filter((col) => col !== null && col !== undefined);

    kanbanColumns.value = convertedKanbanColumns;
  }

  if (frozenColumns && view.frozen_columns) {
    frozenColumns.value = view.frozen_columns
      .map((id) => idToSlugMap[id])
      .filter(Boolean);

    if (tableHeaders && applyFrozenColumnsToHeaders) {
      applyFrozenColumnsToHeaders(tableHeaders, frozenColumns);
    }
  } else if (frozenColumns) {
    frozenColumns.value = [];
  }

  if (tableHeaders && applyColumnWidthsToHeaders) {
    const persistedWidths = view.column_widths;
    const widthsBySlug = {};
    if (persistedWidths && typeof persistedWidths === "object") {
      for (const [id, width] of Object.entries(persistedWidths)) {
        const slug = idToSlugMap[id];
        if (slug && typeof width === "number") {
          widthsBySlug[slug] = width;
        }
      }
    }
    applyColumnWidthsToHeaders(tableHeaders, widthsBySlug);
  }
};

export const loadGroupByOptions = async (
  field,
  entitySlug,
  { currentView = null, projectId = null, preserveSavedConfig = true } = {},
) => {
  try {
    const isTickets = entitySlug === entityTypes.TICKETS;
    const isTasks = entitySlug === entityTypes.TASKS;
    const isDeal = entitySlug === entityTypes.DEALS;

    if (isDeal && field.isPipelineGroupBy && field.pipelineId) {
      const response = await store.dispatch("pipelines/fetchPipelineStages", {
        pipelineId: field.pipelineId,
        force: false,
      });

      if (response.success && response.pipeline_stages) {
        const savedConfig = preserveSavedConfig
          ? currentView?.group_by_config
          : null;
        const savedOptions =
          savedConfig?.attribute_slug === "pipeline_stage_id" &&
          savedConfig?.pipeline_id === field.pipelineId
            ? savedConfig.options || []
            : [];

        const savedOptionsMap = new Map();
        savedOptions.forEach((opt) => {
          savedOptionsMap.set(opt.id, opt);
        });

        const formattedOptions = response.pipeline_stages.map(
          (stage, index) => {
            const savedOption = savedOptionsMap.get(stage.id);

            return {
              id: stage.id,
              value: stage.name,
              visible: savedOption
                ? savedOption.is_visible
                : stage.is_visible !== undefined
                  ? stage.is_visible
                  : true,
              apiId: stage.id,
              position: savedOption ? savedOption.position : index,
            };
          },
        );

        return formattedOptions.sort((a, b) => a.position - b.position);
      }
      return [];
    }

    // Handle deal_stage/pipeline_stage_id without specific pipeline - filter out (not supported)
    const isDealStageField =
      field.id === "deal_stage" ||
      field.slug === "deal_stage" ||
      field.id === "pipeline_stage_id" ||
      field.slug === "pipeline_stage_id";
    if (isDeal && isDealStageField && !field.isPipelineGroupBy) {
      return [];
    }

    if (isTasks) {
      if (!projectId) {
        console.error("Project ID is required for task operations");
        return [];
      }

      const response = await ProjectService.tasks.getTaskAttributeOptions(
        projectId,
        field.slug,
      );

      if (response.success) {
        const optionsArray = response.options || response.attribute?.options;

        if (optionsArray && Array.isArray(optionsArray)) {
          const savedConfig = preserveSavedConfig
            ? currentView?.group_by_config
            : null;
          const savedOptions =
            savedConfig?.attribute_slug === field.slug ||
            savedConfig?.attribute_slug === field.id
              ? savedConfig.options || []
              : [];

          const savedOptionsMap = new Map();
          savedOptions.forEach((opt) => {
            savedOptionsMap.set(opt.id, opt);
          });

          const formattedOptions = optionsArray.map((optionObj, index) => {
            const optionId = optionObj.id;
            const savedOption = savedOptionsMap.get(optionId);

            return {
              id: optionObj.id || index + 1,
              value: optionObj.option || optionObj.value || String(optionObj),
              visible: savedOption
                ? savedOption.is_visible
                : optionObj.is_visible !== undefined
                  ? optionObj.is_visible
                  : true,
              apiId: optionObj.id,
              position: savedOption ? savedOption.position : index,
            };
          });

          return formattedOptions.sort((a, b) => a.position - b.position);
        }
      }
      return [];
    }

    let response;

    if (isTickets) {
      response = await TicketsService.getSchemaAttributeDetails(field.slug);
    } else {
      // Use field.id for detecting special fields since slug can be changed
      const isStatusField =
        standardObjectStatusFields.includes(field.id) ||
        standardObjectStatusFields.includes(field.slug);

      if (isStatusField) {
        const standardObj = standardObjects.find(
          (obj) => obj.slug === entitySlug,
        );
        const entityType = standardObj?.viewSlug || entitySlug;

        const statusResponse = await DashboardService.fetchObjectPropertyStatus(
          entityType,
          "status",
        );

        if (
          statusResponse?.success &&
          statusResponse?.property_details?.options
        ) {
          const savedConfig = preserveSavedConfig
            ? currentView?.group_by_config
            : null;
          // Match saved options using either field.id or field.slug
          const savedOptions =
            savedConfig?.attribute_slug === field.slug ||
            savedConfig?.attribute_slug === field.id
              ? savedConfig.options || []
              : [];

          const savedOptionsMap = new Map();
          savedOptions.forEach((opt) => {
            savedOptionsMap.set(opt.id, opt);
          });

          const formattedOptions = statusResponse.property_details.options.map(
            (option, index) => {
              // Handle both object format (with id) and string format
              const isOptionObject = option && typeof option === "object";
              const optionId =
                isOptionObject && option.id !== undefined
                  ? option.id
                  : toSnakeCase(
                      isOptionObject
                        ? option.option || option.name || option.value
                        : option,
                    );
              const optionName = isOptionObject
                ? option.option || option.name || option.value
                : option;
              const savedOption = savedOptionsMap.get(optionId);

              return {
                id: optionId,
                value: optionName,
                visible: savedOption ? savedOption.is_visible : true,
                apiId: optionId,
                position: savedOption ? savedOption.position : index,
              };
            },
          );

          return formattedOptions.sort((a, b) => a.position - b.position);
        }
        return [];
      }

      
      const isSourceField = field.id === "source" || field.slug === "source";
      if (isSourceField) {
        const savedOptionsMap = buildSavedOptionsMap(field, currentView, preserveSavedConfig);

        const rawSchemaOptions = getSourceOptionsFromAttribute(field);

        if (rawSchemaOptions.length > 0) {
          const formatted = rawSchemaOptions
            .map((opt, index) => {
              const sourceId = String(opt);
              const savedOption = savedOptionsMap.get(sourceId);
              return {
                id: sourceId,
                value: sourceId,
                visible: savedOption ? savedOption.is_visible : true,
                apiId: sourceId,
                position: savedOption ? savedOption.position : index,
              };
            });
          return formatted.sort((a, b) => a.position - b.position);
        }
        return [];
      }

      // Handle industry field - filter out for now as there are too many options
      const isIndustryField =
        field.id === "industry" || field.slug === "industry";
      if (isIndustryField) {
        return [];
      }

      // Handle fields with hardcoded options (company_size, score, deal_type, priority)
      const hardcodedFieldKey = Object.keys(HARDCODED_GROUP_BY_FIELDS).find(
        (key) => field.id === key || field.slug === key,
      );
      if (hardcodedFieldKey) {
        const config = HARDCODED_GROUP_BY_FIELDS[hardcodedFieldKey];
        if (config.dealOnly && !isDeal) {
          // Fall through to API call for non-deal entities
        } else {
          const savedOptionsMap = buildSavedOptionsMap(
            field,
            currentView,
            preserveSavedConfig,
          );
          return formatGroupByOptions(config.options, savedOptionsMap, {
            idKey: config.idKey || "id",
            nameKey: config.nameKey || "name",
          });
        }
      }

      // If field already has options from schema, use them directly — no API call needed
      // Prefer structured_options (has { id, label }) over options (may be plain strings)
      const schemaOptions = field.structured_options?.length > 0
        ? field.structured_options
        : field.options?.length > 0
          ? field.options
          : null;
      if (schemaOptions) {
        const sample = schemaOptions[0];
        const isStringOptions = typeof sample === "string";
        const savedOptionsMap = buildSavedOptionsMap(
          field,
          currentView,
          preserveSavedConfig,
        );
        if (isStringOptions) {
          // Plain string options like ["HIGH", "MEDIUM", "LOW"]
          const formatted = schemaOptions.map((opt, index) => {
            const optionId = toSnakeCase(opt);
            const savedOption = savedOptionsMap.get(optionId);
            return {
              id: optionId,
              value: opt,
              visible: savedOption ? savedOption.is_visible : true,
              apiId: optionId,
              position: savedOption ? savedOption.position : index,
            };
          });
          return formatted.sort((a, b) => a.position - b.position);
        }
        // Object options - detect key names
        const nameKey = sample?.name ? "name" : sample?.label ? "label" : "name";
        return formatGroupByOptions(schemaOptions, savedOptionsMap, {
          idKey: "id",
          nameKey,
        });
      }

      response = await CRMService.getPraticularAttributeDetails(
        entitySlug,
        field.slug,
      );
    }

    if (response?.success) {
      const optionsArray = response.options || response.attribute?.options;

      if (optionsArray && Array.isArray(optionsArray)) {
        const savedConfig = preserveSavedConfig
          ? currentView?.group_by_config
          : null;
        const savedOptions =
          savedConfig?.attribute_slug === field.slug ||
          savedConfig?.attribute_slug === field.id
            ? savedConfig.options || []
            : [];

        const savedOptionsMap = new Map();
        savedOptions.forEach((opt) => {
          savedOptionsMap.set(opt.id, opt);
        });

        const formattedOptions = optionsArray.map((optionObj, index) => {
          // Handle both object format (with id) and string format
          const isOptionObject = optionObj && typeof optionObj === "object";
          const optionId =
            isOptionObject && optionObj.id !== undefined
              ? optionObj.id
              : isOptionObject
                ? optionObj.option || optionObj.value
                : String(optionObj);
          const savedOption = savedOptionsMap.get(optionId);

          return {
            id: optionId,
            value: isOptionObject
              ? optionObj.option || optionObj.value || String(optionObj)
              : String(optionObj),
            visible: savedOption
              ? savedOption.is_visible
              : isOptionObject && optionObj.is_visible !== undefined
                ? optionObj.is_visible
                : true,
            apiId: optionId,
            position: savedOption ? savedOption.position : index,
          };
        });

        return formattedOptions.sort((a, b) => a.position - b.position);
      }
    }

    return [];
  } catch (error) {
    console.error("Error loading group by options:", error);
    return [];
  }
};

export const transformSort = (sorter, existingSorts = {}) => {
  try {
    if (!sorter || !sorter.field) {
      return existingSorts;
    }

    const orderMap = {
      ascend: "asc",
      descend: "desc",
    };

    const newOrder = orderMap[sorter.order];

    if (!newOrder) {
      return {};
    }

    return {
      1: {
        field: sorter.field,
        order: newOrder,
      },
    };
  } catch (error) {
    console.error("Error transforming sorter:", error);
    return existingSorts;
  }
};
